import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, ModalFooter } from '@/components/common';
import { PolicyNumber } from '@/components/common/PolicyNumber';
import { usePolicies } from '@/hooks/usePolicies';
import { formatDate } from '@/utils';
import { useCanState } from '@/hooks/useEmployeeRights';
// By path, not `@/services`: that index is outside this task's file domain.
import { resolveNewQuoteHref } from '@/services/leads';

/**
 * The four things you do to a contact — report items 6 and 7.
 *
 * ONE definition, used from the Contact Details header and from the Contacts
 * list once exactly one row is ticked. Two copies of "what you can do to a
 * contact" is how the list and the detail page end up offering different
 * things for the same person.
 *
 * RAISE CLAIM ASKS WHICH POLICY FIRST. A claim is made against a policy, not
 * against a person, and a contact with three policies has three different
 * answers. Sending them to a blank claim form and hoping they pick the right
 * one is how a claim gets filed against the wrong cover — so the picker is the
 * action, not a step someone can skip. A contact with no policies cannot raise
 * a claim at all, and the button says so rather than opening an empty picker.
 *
 * VIEW POLICIES does not navigate. The policies are already on the Contact
 * Details page, one tab away; sending the reader to a filtered list would take
 * them further from the record they are reading. `onViewPolicies` scrolls to
 * the tab instead. The Contacts list has nowhere to scroll to, so it passes a
 * navigation instead — the difference belongs to the caller, not here.
 */

export interface ContactQuickActionsProps {
  contactId: string;
  contactName?: string;
  /** What "View Policies" should do. The detail page scrolls; the list navigates. */
  onViewPolicies: () => void;
  /** `sm` in the list's selection bar, default in the page header. */
  size?: 'sm' | 'md';
}

export function ContactQuickActions({
  contactId,
  contactName,
  onViewPolicies,
  size = 'md',
}: ContactQuickActionsProps) {
  const navigate = useNavigate();
  const [showPolicyPicker, setShowPolicyPicker] = useState(false);

  // B4/L9: /policies/new, /quotes/new and /claims/new all now require Create
  // on their module. Gating here (not just at the route) stops a click from
  // ever reaching /unauthorized -- and, for the 3 POSPs whose add_policy is
  // POSP_LOCKED, hides Add Policy exactly as plan L9 intends.
  //
  // `useCanState`, not `useCan`: `useCan` throws `isError` away, and a FAILED
  // rights request reads as `false` in every cell. A 502 during the 15-minute
  // gunicorn reload would then delete all three buttons with no explanation,
  // which a user reads as "my rights were revoked". The cells still fail
  // closed -- the buttons stay hidden -- but the message below says the
  // difference out loud, the way ProtectedRoute's RightsGate does.
  const policyRight = useCanState('policies', 'create');
  const quoteRight = useCanState('quotes', 'create');
  const claimRight = useCanState('claims', 'create');
  const canCreatePolicy = policyRight.allowed;
  const canCreateQuote = quoteRight.allowed;
  const canCreateClaim = claimRight.allowed;
  const rightsCheckFailed = !!(policyRight.isError || quoteRight.isError || claimRight.isError);
  // All three read the same `/employee-rights/me/` query, so any one retry
  // refetches for all three. `?.` because a test double may omit it.
  const retryRightsCheck = policyRight.retry ?? quoteRight.retry ?? claimRight.retry;

  // B15: opens on Business Type, prefilled from the contact's most recent
  // converted lead when it has one. `resolveNewQuoteHref` falls back to the
  // plain link itself on a failed or empty lookup, so a broken lead lookup
  // never blocks the ordinary "new quote" action.
  const handleCreateQuote = () => {
    resolveNewQuoteHref(contactId).then(navigate);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {canCreateQuote && (
          <Button variant="outline" size={size} onClick={handleCreateQuote}>
            Create New Quote
          </Button>
        )}
        {canCreatePolicy && (
          <Button
            variant="outline"
            size={size}
            onClick={() => navigate(`/policies/new?customer=${contactId}`)}
          >
            Add Policy
          </Button>
        )}
        {canCreateClaim && (
          <Button variant="outline" size={size} onClick={() => setShowPolicyPicker(true)}>
            Raise Claim
          </Button>
        )}
        <Button variant="outline" size={size} onClick={onViewPolicies}>
          View Policies
        </Button>
      </div>

      {rightsCheckFailed && (
        <p role="alert" className="mt-2 text-sm text-amber-700 dark:text-amber-500">
          We could not check what you are allowed to do for this contact, so those actions are
          hidden. This is a connection problem, not a refusal.{' '}
          {retryRightsCheck && (
            <button type="button" onClick={() => retryRightsCheck()} className="underline">
              Try again
            </button>
          )}
        </p>
      )}

      {showPolicyPicker && (
        <ClaimPolicyPicker
          contactId={contactId}
          contactName={contactName}
          onClose={() => setShowPolicyPicker(false)}
          onPick={(policyId) => navigate(`/claims/new?policyId=${policyId}`)}
        />
      )}
    </>
  );
}

/**
 * Which policy is this claim against?
 *
 * Fetched only when opened -- a picker nobody opens should cost nothing on a
 * page that already loads four lists.
 */
function ClaimPolicyPicker({
  contactId,
  contactName,
  onClose,
  onPick,
}: {
  contactId: string;
  contactName?: string;
  onClose: () => void;
  onPick: (policyId: string) => void;
}) {
  // Live cover only: a claim against an archived or lapsed policy is not a
  // thing a broker should be able to start by accident. `customer_group` is
  // the contact AND its duplicate rows, matching every other list on the page.
  const { data, isLoading, isError } = usePolicies(1, {
    customer_group: contactId,
    display_status: 'active',
    // The live book has a contact with 173 policies. At the default page size
    // this modal showed 20 of them, in server order, with no pager and nothing
    // saying the rest existed -- so "my policy isn't here" would have read as
    // "it isn't covered". 100 is the server's maximum page size; beyond that the
    // count below says plainly that the list is partial.
    page_size: 100,
  });
  const policies = data?.results ?? [];
  const total = data?.count ?? policies.length;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Raise a claim"
      description={
        contactName
          ? `Which of ${contactName}'s policies is this claim against?`
          : 'Which policy is this claim against?'
      }
    >
      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading policies…</p>
      ) : isError ? (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Could not load this contact&apos;s policies. That is not the same as there being none.
        </p>
      ) : policies.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This contact has no active policy to claim against. A claim is made against a policy,
          so add one first.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {total > policies.length && (
            <li className="py-2 text-xs text-amber-700 dark:text-amber-300">
              Showing {policies.length} of {total} active policies. Use the contact&apos;s Policies
              tab if the one you need is not listed.
            </li>
          )}
          {policies.map((policy) => (
            <li key={policy.id}>
              <button
                type="button"
                className="w-full text-left py-3 hover:bg-gray-50 dark:hover:bg-gray-800 px-2 rounded"
                onClick={() => onPick(policy.id)}
              >
                <PolicyNumber source={policy} className="font-medium text-gray-900 dark:text-gray-100" />
                <span className="block text-xs text-gray-500">
                  {policy.insurer_name || 'Insurer not recorded'}
                  {policy.expiry_date ? ` · expires ${formatDate(policy.expiry_date)}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
